import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import type { FormFieldState, FormPreviewResponse, FormState } from '@/types/types';
import { useApi } from '../../providers/api';
import { useToastContext } from '../../providers/toast';
import { fetchAllPages } from '../../utils/pagination';
import { createUniqueId } from '../../utils/print';
import { getResponseErrorMessage } from '../../utils/response';
import { useLabelRevision, usePrintData } from '../print/hooks';
import { inferVariableType } from './formVariableUtils';
import type { FormListItem } from './types';
import {
  buildFieldsPayload,
  buildRulesFromVariables,
  buildVariablesPayload,
  createNewVariable,
  getMissingRequiredAttachedFieldNames,
  hasVariableWithoutFieldAttachment,
  parseFormVariablesFromApi,
} from './utils';

export type { FormListItem } from './types';

type EditorMode = 'closed' | 'create' | 'edit';

// ─── Form State Hook ─────────────────────────────────────────────────────────

const INITIAL_STATE: FormState = {
  name: '',
  description: '',
  labelId: null,
  fields: [],
  variables: [],
  rules: [],
  labelRevisions: [],
  printerIds: [],
  groupIds: [],
  showLabelPreview: true,
};

export const useFormState = () => {
  const [formState, setFormState] = useState<Partial<FormState>>(INITIAL_STATE);

  const updateFormState = useCallback((updates: Partial<FormState>) => {
    setFormState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetForm = useCallback(() => {
    setFormState(INITIAL_STATE);
  }, []);

  return { formState, updateFormState, resetForm };
};

// ─── Forms CRUD Hook ─────────────────────────────────────────────────────────

export const useForms = () => {
  const { client } = useApi();
  const { t } = useTranslation();

  const {
    data: formsData,
    isLoading: formsLoading,
    mutate: mutateForms,
  } = useSWR('forms', async () => {
    const response = await client.v1.forms.$get({ query: { limit: '500' } });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    return response.json();
  });

  const forms = useMemo(() => formsData?.items ?? [], [formsData]);

  const fetchFormDetail = useCallback(
    async (id: number) => {
      const response = await client.v1.forms[':id'].$get({ param: { id: String(id) } });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    [client]
  );

  const deleteForm = useCallback(
    async (id: number): Promise<{ ok: boolean; message?: string }> => {
      const response = await client.v1.forms[':id'].$delete({ param: { id: String(id) } });
      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          t('formPage.response.deleteFailed')
        );
        return { ok: false, message };
      }
      await mutateForms();
      return { ok: true };
    },
    [client, t, mutateForms]
  );

  const saveForm = useCallback(
    async (
      payload: {
        name: string;
        description: string | null;
        labelId: number;
        labelRevisions: string[];
        printerIds: number[];
        groupIds?: number[];
        showLabelPreview: boolean;
        fields: ReturnType<typeof buildFieldsPayload>;
        variables: ReturnType<typeof buildVariablesPayload>;
        rules: ReturnType<typeof buildRulesFromVariables>;
      },
      editingId: number | null
    ): Promise<{ ok: boolean; message?: string }> => {
      const isEditing = editingId !== null;
      const { labelId: _lid, ...updatePayload } = payload;

      const response = isEditing
        ? await client.v1.forms[':id'].$put({
            param: { id: String(editingId) },
            json: updatePayload,
          })
        : await client.v1.forms.$post({ json: payload });

      if (!response.ok) {
        const message = await getResponseErrorMessage(
          response,
          isEditing ? t('formPage.response.updateFailed') : t('formPage.response.createFailed')
        );
        return { ok: false, message };
      }

      await mutateForms();
      return { ok: true };
    },
    [client, t, mutateForms]
  );

  const previewForm = useCallback(
    async (payload: {
      labelId: number;
      name: string;
      description: string | null;
      fields: ReturnType<typeof buildFieldsPayload>;
      variables: ReturnType<typeof buildVariablesPayload>;
      rules: ReturnType<typeof buildRulesFromVariables>;
    }): Promise<FormPreviewResponse> => {
      const response = await client.v1.forms.preview.$post({
        json: {
          labelId: payload.labelId,
          variables: [],
          draft: {
            name: payload.name,
            description: payload.description,
            fields: payload.fields,
            variables: payload.variables,
            rules: payload.rules,
          },
        },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response.json();
    },
    [client]
  );

  return { forms, formsLoading, fetchFormDetail, deleteForm, saveForm, previewForm, mutateForms };
};

// ─── Form Editor Hook ────────────────────────────────────────────────────────

export const useFormEditor = () => {
  const { t } = useTranslation();
  const { client } = useApi();
  const { showToast } = useToastContext();
  const { labels, printers, getLabelRevision } = usePrintData();
  const { formState, updateFormState: applyFormUpdate, resetForm } = useFormState();
  const { forms, formsLoading, fetchFormDetail, deleteForm, saveForm, previewForm } = useForms();

  const {
    labelId,
    labelRevisions,
    printerIds,
    groupIds,
    variables,
    name,
    fields,
    description,
    showLabelPreview,
  } = formState;

  const [editorMode, setEditorMode] = useState<EditorMode>('closed');
  const [editingFormId, setEditingFormId] = useState<number | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreviewValidating, setIsPreviewValidating] = useState(false);
  const [previewResult, setPreviewResult] = useState<FormPreviewResponse | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const isEditing = editingFormId !== null;

  const updateFormState = useCallback(
    (updates: Partial<FormState>) => {
      setHasUnsavedChanges(true);
      setValidationError(null);
      applyFormUpdate(updates);
    },
    [applyFormUpdate]
  );

  // ─── Groups dropdown ─────────────────────────────────────────────────────────

  // Groups are per application and a form may only use those of the applications its label is
  // assigned to. Applications the user cannot manage answer 403 and contribute no options.
  const { data: accessGroups } = useSWR(
    labelId != null ? ['form-group-options', labelId] : null,
    async () => {
      const assignments = await fetchAllPages((query) =>
        client.v1['label-assignments']
          .$get({ query: { ...query, labelIds: String(labelId) } })
          .then((r) => r.json())
      );
      const labelApplications = [...new Set(assignments.items.map((a) => a.application))];
      const lists = await Promise.all(
        labelApplications.map(async (application) => {
          const res = await client.v1['form-access-groups'].$get({ query: { application } });
          if (res.status === 403) return [];
          // Any other failure must not become a partial list: saving would replace the links.
          if (!res.ok) throw new Error(`Failed to load form access groups (${res.status})`);
          const body = (await res.json()) as {
            items: { id: number; groupName: string; application: string }[];
          };
          return body.items;
        })
      );
      return [...new Map(lists.flat().map((group) => [group.id, group])).values()];
    }
  );

  // Options are keyed by group id: the same name can exist in several applications, and the form
  // links to the exact groups picked. The application is shown only when it tells them apart.
  const groupOptions = useMemo(() => {
    const groups = accessGroups ?? [];
    const showApplication = new Set(groups.map((group) => group.application)).size > 1;
    return [...groups]
      .sort((a, b) => a.groupName.localeCompare(b.groupName) || a.id - b.id)
      .map((group) => ({
        value: String(group.id),
        label: showApplication ? `${group.groupName} (${group.application})` : group.groupName,
      }));
  }, [accessGroups]);

  const handleGroupChange = useCallback(
    (values: string[]) => {
      if (values.includes('\x00__all__')) {
        // Nothing to select while the options are loading or none could be fetched.
        if (groupOptions.length === 0) return;
        updateFormState({ groupIds: groupOptions.map((o) => Number(o.value)) });
      } else {
        updateFormState({ groupIds: values.map(Number) });
      }
    },
    [groupOptions, updateFormState]
  );

  // ─── Label revision data ────────────────────────────────────────────────────

  const labelRevisionData = useLabelRevision(
    labelId as number,
    (labelRevisions && labelRevisions.length > 0 ? labelRevisions[0] : '') as string,
    getLabelRevision
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: fields and variables are intentionally excluded to avoid resetting form state on every edit.
  useEffect(() => {
    if (!labelRevisionData) return;
    const existingFieldIds = new Map((fields ?? []).map((field) => [field.name, field.id]));

    const updatedFields = labelRevisionData.fields.map((field) => ({
      ...field,
      id: existingFieldIds.get(field.name) ?? createUniqueId(),
    }));

    const nextVariables = variables ?? [];

    const updatedVariables = nextVariables.map((v) => {
      if (v.fieldId || !v.labelFieldMapping) return v;
      const matchingField = updatedFields.find((f) => f.name === v.labelFieldMapping);
      return matchingField ? { ...v, fieldId: matchingField.id } : v;
    });

    applyFormUpdate({ fields: updatedFields, variables: updatedVariables });
  }, [labelRevisionData, applyFormUpdate]);

  // ─── Computed options ───────────────────────────────────────────────────────

  const selectedLabel = useMemo(
    () => labels?.items.find((label) => label.id === labelId) ?? null,
    [labelId, labels?.items]
  );

  const labelOptions = useMemo(
    () =>
      labels?.items.map((label) => ({ value: label.id?.toString() ?? '', label: label.name })) ??
      [],
    [labels]
  );

  const labelRevisionOptions = useMemo(
    () => selectedLabel?.availableRevisions.map((rev) => ({ value: rev, label: rev })) ?? [],
    [selectedLabel]
  );

  const printerOptions = useMemo(
    () => printers?.items.map((p) => ({ value: p.id?.toString() ?? '', label: p.name })) ?? [],
    [printers]
  );

  const selectedPrinterValues = useMemo(
    () => printerIds?.map((id) => id.toString()) ?? [],
    [printerIds]
  );

  // ─── Variable handlers ──────────────────────────────────────────────────────

  const handleVariableChange = useCallback(
    (id: string, value: Record<string, unknown>) => {
      updateFormState({
        variables: variables?.map((v) => (v.id === id ? { ...v, ...value } : v)),
      });
    },
    [variables, updateFormState]
  );

  const handleRemoveVariable = useCallback(
    (id: string) => {
      updateFormState({ variables: variables?.filter((v) => v.id !== id) ?? [] });
    },
    [variables, updateFormState]
  );

  const handleAddVariable = useCallback(
    (field: FormFieldState) => {
      if (!field.id) return;
      const next = {
        ...createNewVariable(),
        fieldId: field.id,
        labelFieldMapping: field.name,
        type: inferVariableType(field),
      };
      updateFormState({ variables: [...(variables || []), next] });
    },
    [variables, updateFormState]
  );

  // ─── Printer handler ────────────────────────────────────────────────────────

  const handlePrinterChange = useCallback(
    (values: string[]) => {
      updateFormState({ printerIds: values.map(Number).filter(Number.isInteger) });
    },
    [updateFormState]
  );

  // ─── Validation ─────────────────────────────────────────────────────────────

  const getValidationError = useCallback((): string | null => {
    if (!name?.trim() || !labelId || !labelRevisions?.length) return 'field_errors';
    if (hasVariableWithoutFieldAttachment(variables ?? [], fields ?? [])) return 'field_errors';
    const missing = getMissingRequiredAttachedFieldNames(fields ?? [], variables ?? [], 'name');
    if (missing.length > 0)
      return t('formPage.response.addRequiredFieldNames', { fields: missing.join(', ') });
    return null;
  }, [name, labelId, labelRevisions, variables, fields, t]);

  const buildPayload = useCallback(
    () => ({
      name: name!.trim(),
      description: description?.trim() || null,
      labelId: labelId!,
      labelRevisions: labelRevisions!,
      printerIds: printerIds ?? [],
      // Only ids the user can pick for this label are sent; others (another label's, or groups
      // of applications the user cannot manage) are neither shown nor changed. While the options
      // are not loaded nothing is sent, which leaves the form's groups as they are.
      groupIds: accessGroups
        ? (groupIds ?? []).filter((id) => accessGroups.some((group) => group.id === id))
        : undefined,
      showLabelPreview: showLabelPreview ?? true,
      fields: buildFieldsPayload(fields ?? [], variables ?? []),
      variables: buildVariablesPayload(variables ?? [], fields ?? []),
      rules: buildRulesFromVariables(variables ?? []),
    }),
    [
      name,
      labelId,
      fields,
      variables,
      description,
      labelRevisions,
      printerIds,
      groupIds,
      accessGroups,
      showLabelPreview,
    ]
  );

  const validateForm = useCallback(() => {
    const error = getValidationError();
    if (error) {
      setSubmitAttempted(true);
      setValidationError(error === 'field_errors' ? null : error);
      return null;
    }
    setValidationError(null);
    return buildPayload();
  }, [getValidationError, buildPayload]);

  // ─── Editor actions ─────────────────────────────────────────────────────────

  const resetEditorState = useCallback(() => {
    setEditingFormId(null);
    setHasUnsavedChanges(false);
    setPreviewResult(null);
    setValidationError(null);
    setSubmitAttempted(false);
    resetForm();
  }, [resetForm]);

  const openCreate = useCallback(() => {
    resetEditorState();
    setEditorMode('create');
  }, [resetEditorState]);

  const openEdit = useCallback(
    async (form: FormListItem) => {
      setEditingFormId(form.id);
      try {
        const data = await fetchFormDetail(form.id);
        const apiVariables = parseFormVariablesFromApi(
          data as unknown as Parameters<typeof parseFormVariablesFromApi>[0],
          createUniqueId
        );

        applyFormUpdate({
          name: data.name ?? '',
          description: data.description ?? '',
          labelId: data.labelId,
          labelRevisions: data.labelRevisions ?? [],
          printerIds: data.printerIds ?? [],
          groupIds: data.groupIds ?? [],
          showLabelPreview: data.showLabelPreview ?? true,
          variables: apiVariables,
        });
        setHasUnsavedChanges(false);
        setPreviewResult(null);
        setEditorMode('edit');
      } catch {
        showToast(t('formPage.response.loadFormDetailsFailed'), 'error');
      }
    },
    [fetchFormDetail, applyFormUpdate, showToast, t]
  );

  const closeEditor = useCallback(() => {
    resetEditorState();
    setEditorMode('closed');
  }, [resetEditorState]);

  const handleSave = useCallback(async () => {
    const payload = validateForm();
    if (!payload) return;

    setIsSubmitting(true);
    try {
      const result = await saveForm(payload, editingFormId);
      if (!result.ok) {
        showToast(result.message!, 'error');
        return;
      }
      showToast(
        isEditing ? t('formPage.response.updateSuccess') : t('formPage.response.createSuccess'),
        'success'
      );
      closeEditor();
    } catch (error) {
      const fallback = isEditing
        ? t('formPage.response.updateFailed')
        : t('formPage.response.createFailed');
      showToast(error instanceof Error ? error.message : fallback, 'error');
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, saveForm, editingFormId, isEditing, closeEditor, showToast, t]);

  const handleDelete = useCallback(
    async (id: number) => {
      setIsDeleting(true);
      try {
        const result = await deleteForm(id);
        if (!result.ok) {
          showToast(result.message!, 'error');
          return;
        }
        showToast(t('formPage.response.deleteSuccess'), 'success');
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : t('formPage.response.deleteFailed'),
          'error'
        );
      } finally {
        setIsDeleting(false);
      }
    },
    [deleteForm, showToast, t]
  );

  const handlePreview = useCallback(async (): Promise<boolean> => {
    const payload = validateForm();
    if (!payload) return false;

    setIsPreviewValidating(true);
    try {
      const result = await previewForm(payload);
      setPreviewResult(result);
      return true;
    } catch {
      setPreviewResult(null);
      showToast(t('formPage.response.previewFailed'), 'warning');
      return false;
    } finally {
      setIsPreviewValidating(false);
    }
  }, [validateForm, previewForm, showToast, t]);

  return {
    // State
    formState,
    forms,
    formsLoading,
    editorMode,
    editingFormId,
    isEditing,
    isSubmitting,
    isDeleting,
    isPreviewValidating,
    hasUnsavedChanges,
    validationError,
    submitAttempted,
    previewResult,

    // Groups
    groupOptions,
    handleGroupChange,

    // Options
    labels,
    printers,
    selectedLabel,
    labelOptions,
    labelRevisionOptions,
    printerOptions,
    selectedPrinterValues,

    // Handlers
    updateFormState,
    handleVariableChange,
    handleRemoveVariable,
    handleAddVariable,
    handlePrinterChange,
    openCreate,
    openEdit,
    closeEditor,
    handleSave,
    handleDelete,
    handlePreview,
  };
};
